import React, { useState } from 'react';
import { Eye, Edit, Trash2, Shield, ShieldCheck, UserCheck, RotateCcw } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { UserDto } from '../../types';
import { BatchAssignIdentifiersModal } from './BatchAssignIdentifiersModal';
import { formatDistanceToNow } from 'date-fns';

interface UserTableProps {
  users: UserDto[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  isLoading: boolean;
  error: any;
  divisionId: string;
  onPageChange: (page: number) => void;
  onUserClick: (userId: string) => void;
  onDeleteUser: (userId: string) => void;
  onDeleteUsers?: (userIds: string[]) => Promise<void>;
  onActivateUser?: (userId: string) => void;
  onRefresh?: () => void;
  isDeleting: boolean;
  isActivating?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  readOnly?: boolean;
  /** Deleting (deactivating) users is restricted to Admin/DivisionAdmin - WorkflowAdmin can
   * create/edit/reactivate users but not delete them. Defaults to true for callers that don't care. */
  canDelete?: boolean;
}

export function UserTable({
  users,
  totalCount,
  currentPage,
  pageSize,
  totalPages,
  isLoading,
  error,
  divisionId,
  onPageChange,
  onUserClick,
  onDeleteUser,
  onDeleteUsers,
  onActivateUser,
  onRefresh,
  isDeleting,
  isActivating = false,
  sortBy = 'firstName',
  sortOrder = 'asc',
  onSort,
  readOnly = false,
  canDelete = true,
}: UserTableProps) {
  const [selectedUsers, setSelectedUsers] = useState<UserDto[]>([]);
  const [showBatchAssignModal, setShowBatchAssignModal] = useState(false);

  const handleSelectAll = (checked: boolean) => {
    setSelectedUsers(checked ? users : []);
  };

  const handleSelectUser = (user: UserDto, checked: boolean) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, user]);
    } else {
      setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
    }
  };

  const isAllSelected = users.length > 0 && selectedUsers.length === users.length;
  const isPartiallySelected = selectedUsers.length > 0 && selectedUsers.length < users.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-secondary-600 dark:text-secondary-400">Loading users...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="text-red-600 dark:text-red-400 mb-2">Failed to load users</div>
          <div className="text-sm text-secondary-500 dark:text-secondary-400">
            {error?.message || 'An unexpected error occurred'}
          </div>
        </div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="text-secondary-600 dark:text-secondary-400 mb-2">No users found</div>
          <div className="text-sm text-secondary-500 dark:text-secondary-400">
            Try adjusting your search criteria or filters
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadge = (isActive: boolean) => {
    return (
      <Badge variant={isActive ? 'success' : 'error'}>
        {isActive ? 'Active' : 'Inactive'}
      </Badge>
    );
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      'Admin': { variant: 'error' as const, icon: ShieldCheck },
      'DivisionAdmin': { variant: 'warning' as const, icon: Shield },
      'Approver': { variant: 'info' as const, icon: Shield },
      'Shopper': { variant: 'default' as const, icon: null },
      'Vendor': { variant: 'success' as const, icon: null },
    };

    // Use predefined config if available, otherwise use a default style for custom roles
    const config = roleConfig[role as keyof typeof roleConfig] || { 
      variant: 'info' as const, 
      icon: null 
    };
    const Icon = config.icon;

    return (
      <Badge variant={config.variant}>
        <div className="flex items-center space-x-1">
          {Icon && <Icon className="h-3 w-3" />}
          <span>{role}</span>
        </div>
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      {/* Batch Actions */}
      {selectedUsers.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <span className="font-medium text-blue-800">
                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedUsers([])}
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
              >
                Clear Selection
              </Button>
              <Button
                size="sm"
                onClick={() => setShowBatchAssignModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Assign Match Identifiers
              </Button>
              {!readOnly && canDelete && onDeleteUsers && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    const ids = selectedUsers.map(u => u.id);
                    await onDeleteUsers(ids);
                    setSelectedUsers([]);
                  }}
                  className="flex items-center gap-1 text-red-600 dark:text-red-400 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Selected
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={input => {
                  if (input) input.indeterminate = isPartiallySelected;
                }}
                onChange={(e) => handleSelectAll(e.target.checked)}
                aria-label={isAllSelected ? 'Deselect all users' : 'Select all users'}
                className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 dark:border-secondary-600 rounded bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              />
            </TableHead>
            <TableHead
              sortable={!!onSort}
              sortDirection={sortBy === 'firstName' ? sortOrder : null}
              onSort={() => onSort && onSort('firstName')}
            >
              Name
            </TableHead>
            <TableHead
              sortable={!!onSort}
              sortDirection={sortBy === 'email' ? sortOrder : null}
              onSort={() => onSort && onSort('email')}
            >
              Email
            </TableHead>
            <TableHead
              sortable={!!onSort}
              sortDirection={sortBy === 'externalUserId' ? sortOrder : null}
              onSort={() => onSort && onSort('externalUserId')}
            >
              External ID
            </TableHead>
            <TableHead
              sortable={!!onSort}
              sortDirection={sortBy === 'role' ? sortOrder : null}
              onSort={() => onSort && onSort('role')}
            >
              Role
            </TableHead>
            <TableHead
              sortable={!!onSort}
              sortDirection={sortBy === 'active' ? sortOrder : null}
              onSort={() => onSort && onSort('active')}
            >
              Status
            </TableHead>
            <TableHead
              sortable={!!onSort}
              sortDirection={sortBy === 'lastLogin' ? sortOrder : null}
              onSort={() => onSort && onSort('lastLogin')}
            >
              Last Login
            </TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            const isSelected = selectedUsers.some(u => u.id === user.id);
            
            return (
              <TableRow key={user.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-700" data-testid={`user-row-${user.id}`}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleSelectUser(user, e.target.checked)}
                    aria-label={`Select ${user.firstName} ${user.lastName}`}
                    className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 border-gray-300 dark:border-secondary-600 rounded bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  />
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium text-secondary-900 dark:text-white">
                      {user.firstName} {user.lastName}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-secondary-900 dark:text-white" data-testid={`user-email-${user.id}`}>{user.email}</div>
                </TableCell>
                <TableCell>
                  <div className="text-secondary-900 dark:text-white">{user.externalUserId || 'N/A'}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {user.divisionRoles && user.divisionRoles.length > 0 ? (
                      user.divisionRoles.map((divRole, index) => (
                        <div key={index} className="flex items-center">
                          {getRoleBadge(divRole.role)}
                        </div>
                      ))
                    ) : (
                      <span className="text-secondary-500 dark:text-secondary-400">No role assigned</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {getStatusBadge(user.active)}
                  {!user.active && user.deactivatedAt && (
                    <div
                      className="text-xs text-secondary-500 dark:text-secondary-400 mt-1"
                      title={`Deactivated ${new Date(user.deactivatedAt).toLocaleString()}`}
                    >
                      {user.deactivationReason || 'Manual'} &middot; {formatDistanceToNow(new Date(user.deactivatedAt), { addSuffix: true })}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-secondary-900 dark:text-white text-sm">
                    {user.lastLoginAt 
                      ? formatDistanceToNow(new Date(user.lastLoginAt), { addSuffix: true })
                      : <span className="text-secondary-500 dark:text-secondary-400 italic">Never</span>
                    }
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onUserClick(user.id)}
                      data-testid={`view-btn-${user.id}`}
                      className="flex items-center space-x-1"
                    >
                      <Eye className="h-4 w-4" />
                      <span>View</span>
                    </Button>
                    {!readOnly && !user.active && onActivateUser && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onActivateUser(user.id)}
                        disabled={isActivating}
                        data-testid={`activate-btn-${user.id}`}
                        className="flex items-center space-x-1 text-green-600 dark:text-green-400 hover:text-green-700 hover:bg-green-50"
                      >
                        <RotateCcw className="h-4 w-4" />
                        <span>Reactivate</span>
                      </Button>
                    )}
                    {!readOnly && canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteUser(user.id)}
                        disabled={isDeleting}
                        data-testid={`delete-btn-${user.id}`}
                        className="flex items-center space-x-1 text-red-600 dark:text-red-400 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Delete</span>
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-secondary-700 dark:text-secondary-200">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              Previous
            </Button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  // Show pages around current page
                  const start = Math.max(1, currentPage - 2);
                  const end = Math.min(totalPages, currentPage + 2);
                  return page >= start && page <= end;
                })
                .map(page => (
                  <Button
                    key={page}
                    variant={page === currentPage ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => onPageChange(page)}
                    className="w-8 h-8 p-0"
                  >
                    {page}
                  </Button>
                ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Batch Assign Identifiers Modal */}
      <BatchAssignIdentifiersModal
        isOpen={showBatchAssignModal}
        onClose={() => setShowBatchAssignModal(false)}
        divisionId={divisionId}
        selectedUsers={selectedUsers}
        onSuccess={() => {
          setSelectedUsers([]);
          onRefresh?.();
        }}
      />
    </div>
  );
}
